import { useEffect, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/app/store";
import {
  PUBLISHED_APP_PREFETCH_MAX_APPS,
  PUBLISHED_APP_PREFETCH_START_DELAY_MS,
  STATUS,
} from "@/constants";
import { buildPublishedAppItemsStreamSource } from "@/features/published-apps/hooks/usePublishedAppItemsStream";
import { prefetchAppItemsStream } from "@/features/published-apps/lib/publishedAppItemsPrefetch";
import { isAppFavorite } from "@/features/published-apps/lib/publishedAppsGrouping";
import {
  prefetchPublishedAppSnapshots,
  prefetchPublishedTabFilterFields,
} from "@/features/published-apps/lib/publishedAppViewCache";
import {
  fetchPublishedAppById,
  fetchPublishedApps,
} from "@/features/published-apps/model/publishedAppsSlice";

/** The store's dispatch, which the prefetch needs to warm the app detail through its thunk. */
type Dispatch = ReturnType<typeof useAppDispatch>;

/**
 * Issue every request `/apps/:id` makes, for one app, into the caches that view reads from:
 * the app detail, its snapshot list, its default tab's filter fields, and its widget stream.
 *
 * The three small reads go together — they are independent and none blocks the others — and the
 * stream is awaited last so the caller can warm favourites one at a time.
 */
async function prefetchPublishedAppView(appId: number, dispatch: Dispatch): Promise<void> {
  await Promise.allSettled([
    dispatch(fetchPublishedAppById(appId)),
    prefetchPublishedAppSnapshots(appId),
    // `null` is the tab the view opens on, before the viewer has picked one.
    prefetchPublishedTabFilterFields(appId, null),
  ]);

  const source = buildPublishedAppItemsStreamSource(appId);
  await prefetchAppItemsStream(source.key, source.path, source.fetcher);
}

/**
 * Warms the viewer's favourite apps in the background once they are signed in, so opening one from
 * the home page renders from memory instead of waiting on the server to run its widget queries.
 *
 * Favourites are warmed **one at a time**, and only `PUBLISHED_APP_PREFETCH_MAX_APPS` of them. Each
 * app's stream holds a connection open for as long as its slowest query runs, so warming several at
 * once would spend the browser's per-host connection budget on work the viewer has not asked for
 * yet — and starve the page they are actually looking at.
 *
 * Every request goes through a cache that dedupes, so a viewer who opens a favourite mid-prefetch
 * joins the work already in flight rather than duplicating it.
 */
export function useFavoritePublishedAppsPrefetch(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const dispatch = useAppDispatch();
  const status = useAppSelector((state) => state.publishedApps.status);
  const apps = useAppSelector((state) => state.publishedApps.apps);

  const queuedAppIdsRef = useRef<number[]>([]);
  const startedAppIdsRef = useRef(new Set<number>());
  const isDrainingRef = useRef(false);
  const isStoppedRef = useRef(false);
  const startTimerRef = useRef<number | null>(null);

  // The list is what identifies the favourites, and nothing else guarantees it has been asked for:
  // the landing page only lists apps when the viewer can see that widget. The thunk's own condition
  // makes this a no-op when a page has already requested it.
  useEffect(() => {
    if (!enabled) {
      return;
    }
    void dispatch(fetchPublishedApps());
  }, [dispatch, enabled]);

  const favoriteAppIds = useMemo(
    () =>
      apps
        .filter(isAppFavorite)
        .map((app) => app.id)
        .slice(0, PUBLISHED_APP_PREFETCH_MAX_APPS),
    [apps],
  );

  useEffect(() => {
    // Unmounting means the session is over (this runs from the signed-in layout), so stop rather
    // than keep warming apps nobody is going to open. Tearing down only here, and never when the
    // effect below re-runs, is deliberate: a favourite toggled mid-prefetch re-runs it, and
    // cancelling the drain there would abandon every app still queued behind the current one.
    isStoppedRef.current = false;
    return () => {
      isStoppedRef.current = true;
      if (startTimerRef.current !== null) {
        window.clearTimeout(startTimerRef.current);
        startTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!enabled || status !== STATUS.SUCCEEDED) {
      return;
    }

    const newlyQueued = favoriteAppIds.filter((appId) => !startedAppIdsRef.current.has(appId));
    if (newlyQueued.length === 0) {
      return;
    }
    for (const appId of newlyQueued) {
      startedAppIdsRef.current.add(appId);
      queuedAppIdsRef.current.push(appId);
    }
    if (isDrainingRef.current) {
      // The running drain picks these up: appending to a queue it is still reading beats starting a
      // second one, which would put two streams on the wire at once.
      return;
    }

    isDrainingRef.current = true;
    // Behind a delay so the requests the landing page renders from are not queued behind a prefetch
    // for a page the viewer has not opened.
    startTimerRef.current = window.setTimeout(() => {
      startTimerRef.current = null;
      void (async () => {
        try {
          while (queuedAppIdsRef.current.length > 0 && !isStoppedRef.current) {
            const appId = queuedAppIdsRef.current.shift();
            if (appId === undefined) {
              break;
            }
            // Best effort throughout: a favourite that fails to warm simply loads normally when
            // opened, so one failure must not stop the rest from warming.
            await prefetchPublishedAppView(appId, dispatch).catch(() => {});
          }
        } finally {
          isDrainingRef.current = false;
        }
      })();
    }, PUBLISHED_APP_PREFETCH_START_DELAY_MS);
  }, [dispatch, enabled, status, favoriteAppIds]);
}
